import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Trans } from 'lingui-react';
import { camelCase, compact } from 'lodash';

import UnreadNoteLabel from 'containers/UnreadNoteLabelContainer';

import ETR from 'components/_common/ETR';
import FollowUpLabel from 'components/_common/FollowUpLabel';
import SeverityLabel from 'components/SeverityLabel';

import BorderedStatus from 'elements/BorderedStatus';
import Link from 'elements/Link';
import { ListTable } from 'base-components';
import Popover, {
  PopoverTarget,
  PopoverContent,
  ScrollingPopoverContentWrapper,
} from 'elements/Popover';
import StatusSpan from 'elements/StatusSpan';
import Span from 'elements/Span';
import TextDiv from 'elements/TextDiv';

import { getOutputText } from 'utils/widget';

import getComplaintText from 'utils/complaint';

import StatusPopoverContent from './StatusPopoverContent';
import StatusPopoverTable from './StatusPopoverTable';
import messages from './messages';
import { getCaseCreated } from './helpers';

function getRowModifier({ selectionDisabled, isSelected }) {
  if (selectionDisabled) {
    return undefined;
  }
  if (isSelected) {
    return 'selected';
  }
  return 'selectableHover';
}

function CaseListRow({
  caseInfo,
  assetInfo,
  onSelect,
  isSelected,
  selectionDisabled,
}) {
  const caseId = caseInfo.get('id');
  const assetUnitNumber = assetInfo.get('unitNumber');
  const assetVINNumber = assetInfo.get('vinNumber');
  const providerName = caseInfo.getIn(['defaultRecipient', 'group', 'companyName']);
  const {
    title: complaintTitle,
    content: complaintContent,
  } = getComplaintText(caseInfo.toJS(), { titleInLink: false });
  const closedAt = caseInfo.get('closedAt');
  const openOrClosed = closedAt ? 'closed' : 'open';
  const repairStatus = caseInfo.get('repairStatus');
  const estimateTotal = caseInfo.get('estimateTotal');
  const approvalStatus = caseInfo.get('approvalStatus');
  const approvalStatusModifier = approvalStatus && camelCase(approvalStatus);
  const caseCreated = getCaseCreated(caseInfo);
  const severityColor = caseInfo.get('severityColor');
  const severityCount = Number(caseInfo.get('severityCount'));
  const followUpColor = caseInfo.get('followUpColor');
  const followUpTime = caseInfo.get('followUpTime');
  const rowModifier = getRowModifier({ selectionDisabled, isSelected });
  return (
    <ListTable.Row
      modifiers={compact(['lined', rowModifier])}
      onClick={onSelect}
    >
      <ListTable.Data>
        <Link
          to={`/cases/${caseId}`}
          modifiers={['heavy', 'uppercase']}
        >
          <Trans id={messages.title} values={{ caseId }} />
        </Link>
      </ListTable.Data>
      <ListTable.Data>
        <Popover
          showOnHover
          position="right"
          positionOffset={35}
        >
          <PopoverTarget>
            <BorderedStatus modifiers={[openOrClosed]}>{openOrClosed}</BorderedStatus>
          </PopoverTarget>
          <PopoverContent>
            <StatusPopoverContent>
              <TextDiv modifiers={['uppercase', 'xSmallText']}>
                <Trans id={messages.statusPopoverTitle} />
              </TextDiv>
              <StatusPopoverTable modifiers={['smallText']}>
                <tbody>
                  <tr>
                    <th><Trans id={messages.statusPopoverCaseStatus} /></th>
                    <td>
                      <BorderedStatus modifiers={[openOrClosed]}>{openOrClosed}</BorderedStatus>
                    </td>
                  </tr>
                  <tr>
                    <th><Trans id={messages.statusPopoverRepairStatus} /></th>
                    <td>{getOutputText(repairStatus, messages.status)}</td>
                  </tr>
                </tbody>
              </StatusPopoverTable>
            </StatusPopoverContent>
          </PopoverContent>
        </Popover>
      </ListTable.Data>
      <ListTable.Data>
        <Link to={`/assets/${assetInfo.get('id')}`}>
          {getOutputText(assetUnitNumber, messages.unit)}
        </Link>
      </ListTable.Data>
      <ListTable.Data flex={1.5} minWidth={150}>
        {getOutputText(assetVINNumber)}
      </ListTable.Data>
      <ListTable.Data flex={1.5} minWidth={150}>
        <TextDiv modifiers={['ellipsis', 'short', 'smallWidth']}>
          {getOutputText(providerName)}
        </TextDiv>
      </ListTable.Data>
      <ListTable.Data flex={2} minWidth={200}>
        <Popover showOnHover>
          <PopoverTarget>
            <TextDiv
              modifiers={[
                'bottomGap',
                'heavyText',
                'ellipsis',
                'oneLine',
                'smallText',
                'smallWidth',
              ]}
            >
              {complaintTitle}
            </TextDiv>
            <TextDiv modifiers={['ellipsis', 'short', 'smallWidth']}>
              {complaintContent}
            </TextDiv>
          </PopoverTarget>
          <PopoverContent>
            <ScrollingPopoverContentWrapper modifiers={['smallText']}>
              <TextDiv modifiers={['bold', 'bright', 'bottomGap']}>
                {complaintTitle}
              </TextDiv>
              <TextDiv modifiers={['bottomGap']}>
                {complaintContent}
              </TextDiv>
            </ScrollingPopoverContentWrapper>
          </PopoverContent>
        </Popover>
      </ListTable.Data>
      <ListTable.Data>
        <TextDiv>
          {getOutputText(estimateTotal, messages.estimate)}
        </TextDiv>
        <StatusSpan modifiers={compact([approvalStatusModifier, 'bold', 'tall', 'uppercase'])}>
          {approvalStatus}
        </StatusSpan>
      </ListTable.Data>
      <ListTable.Data className="etr">
        <ETR caseInfo={caseInfo} maxWidth="6rem" />
      </ListTable.Data>
      <ListTable.Data>
        <Span modifiers={['capitalize']}>
          {getOutputText(caseCreated, messages.created)}
        </Span>
      </ListTable.Data>
      <ListTable.Data>
        <SeverityLabel
          color={severityColor}
          value={severityCount}
          small
        />
      </ListTable.Data>
      <ListTable.Data flex={1.5} minWidth={150}>
        <UnreadNoteLabel hasModal caseInfo={caseInfo} />
      </ListTable.Data>
      <ListTable.Data>
        <FollowUpLabel
          followUpColor={followUpColor}
          followUpTime={followUpTime}
        />
      </ListTable.Data>
    </ListTable.Row>
  );
}

CaseListRow.propTypes = {
  assetInfo: ImmutablePropTypes.contains({
    id: PropTypes.string,
  }).isRequired,
  caseInfo: ImmutablePropTypes.contains({
    id: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  selectionDisabled: PropTypes.bool.isRequired,
};

export default CaseListRow;



// WEBPACK FOOTER //
// ./components/CaseDisplayElement/CaseListRow.js